/**
 * ScreenshotStudio
 * Renders inside the Handbook hero. Gives a Main Admin / Secretariat user a
 * one-click button that re-captures every screenshot used in the Handbook PDF
 * and Platform Presentation. Polls /api/screenshots/status while a run is in
 * progress and shows per-image success/failure.
 */
import React, { useEffect, useRef, useState } from 'react';
import { RefreshCw, CheckCircle2, XCircle, Loader2, Camera } from 'lucide-react';

const authHeader = () => {
  if (typeof window === 'undefined') return {};
  const candidates = ['adminToken', 'admin_token', 'v2.1.0.church.adminToken'];
  for (const k of candidates) {
    const v = localStorage.getItem(k);
    if (v) return { 'x-admin-token': v };
  }
  for (const k of Object.keys(localStorage)) {
    if (/admin.?token/i.test(k) && localStorage.getItem(k)) {
      return { 'x-admin-token': localStorage.getItem(k) };
    }
  }
  return {};
};

const ScreenshotStudio = () => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [starting, setStarting] = useState(false);
  const pollRef = useRef(null);

  const backend = process.env.REACT_APP_BACKEND_URL || '';

  const fetchStatus = async () => {
    try {
      const r = await fetch(`${backend}/api/screenshots/status`, { headers: authHeader() });
      if (r.ok) setStatus(await r.json());
    } catch (_) {}
  };

  useEffect(() => {
    if (!open) return undefined;
    fetchStatus();
    return undefined;
  }, [open]); // eslint-disable-line

  useEffect(() => {
    if (status?.state === 'running') {
      if (!pollRef.current) {
        pollRef.current = setInterval(fetchStatus, 2000);
      }
    } else if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [status?.state]); // eslint-disable-line

  const startRun = async () => {
    setStarting(true);
    try {
      const r = await fetch(`${backend}/api/screenshots/refresh`, {
        method: 'POST',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (r.status === 403) { alert('Only Main Admin or Secretariat can refresh screenshots.'); return; }
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setStatus({ state: 'running', done: 0, total: 0, results: [] });
      fetchStatus();
    } catch (e) {
      alert(`Failed to start screenshot run: ${e.message || e}`);
    } finally {
      setStarting(false);
    }
  };

  const running = status?.state === 'running';
  const pct = status?.total ? Math.round((status.done / status.total) * 100) : 0;

  return (
    <div className="handbook-noprint mt-4" data-testid="screenshot-studio">
      <button
        onClick={() => setOpen(v => !v)}
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-semibold transition-colors border border-white/20"
        data-testid="open-screenshot-studio"
      >
        <Camera className="w-3.5 h-3.5" />
        {open ? 'Hide Screenshot Studio' : 'Screenshot Studio'}
      </button>

      {open && (
        <div className="mt-3 p-4 rounded-2xl bg-white/10 backdrop-blur border border-white/20 text-white max-w-xl">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div>
              <p className="font-bold text-sm flex items-center gap-2">
                <RefreshCw className="w-4 h-4" /> Refresh all Handbook / Presentation screenshots
              </p>
              <p className="text-xs text-indigo-200/80 mt-1">
                Runs headless Chromium against the live site and overwrites <code>/handbook/*.jpg</code>.
                Both the PDF and the slideshow read from the same folder.
              </p>
            </div>
            <button
              onClick={startRun}
              disabled={starting || running}
              data-testid="run-screenshot-capture"
              className="shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-400 text-indigo-900 text-sm font-bold hover:bg-amber-300 transition-colors disabled:opacity-50 disabled:cursor-wait"
            >
              {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              {running ? 'Capturing…' : starting ? 'Starting…' : 'Start capture'}
            </button>
          </div>

          {status && (
            <>
              <div className="flex items-center justify-between text-xs text-indigo-100/90 mb-1">
                <span className="capitalize">State: <strong>{status.state}</strong></span>
                {status.total > 0 && <span>{status.done} / {status.total}</span>}
              </div>
              <div className="h-2 rounded-full bg-black/30 overflow-hidden">
                <div
                  className={`h-full transition-all ${status.state === 'failed' ? 'bg-red-400' : 'bg-amber-400'}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              {status.current && <p className="text-[11px] text-indigo-200 mt-1">Currently: <code>{status.current}</code></p>}

              {(status.results || []).length > 0 && (
                <ul className="mt-3 max-h-52 overflow-y-auto text-xs space-y-1 pr-1">
                  {status.results.map((r, idx) => (
                    <li key={`${r.name}-${idx}`} className="flex items-center gap-2">
                      {r.ok
                        ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-300 shrink-0" />
                        : <XCircle className="w-3.5 h-3.5 text-red-300 shrink-0" />}
                      <span className="font-mono text-indigo-100/90 w-24 truncate">{r.name}</span>
                      {r.ok
                        ? <span className="text-indigo-200/70">saved ({Math.round((r.size || 0) / 1024)} KB)</span>
                        : <span className="text-red-200/80 truncate">{r.error || 'failed'}</span>}
                    </li>
                  ))}
                </ul>
              )}

              {status.state === 'completed' && (
                <p className="mt-3 text-xs text-emerald-200">
                  Done. Reload the Handbook / Presentation to see the new images
                  (your browser may cache the old ones — hard-refresh helps).
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ScreenshotStudio;
